import { useRef, useState } from 'react';
import { ImageOff, Upload, X, Link as LinkIcon, Plus } from 'lucide-react';
import { cn } from '../utils/cn';

export interface ImageGalleryProps {
  /** Ordered list of image sources — each can be a remote URL or a data URL (base64). */
  value: string[];
  onChange: (next: string[]) => void;
  label?: string;
  /** If true, shows an info strip below the gallery explaining primary-only persistence. */
  primaryNote?: string;
  className?: string;
}

/**
 * Manage a list of product images. Users can:
 * - Paste a URL and press the + button
 * - Drag & drop files, or click "Upload" to browse
 * - Files are read as base64 data URLs for client-side preview
 * - Reorder by clicking "Make primary"
 * - Remove individual images
 *
 * The first image is considered primary; the consuming form can persist only
 * that one if the backend doesn't yet support a ProductImage collection.
 */
export function ImageGallery({
  value,
  onChange,
  label,
  primaryNote,
  className,
}: ImageGalleryProps) {
  const [urlDraft, setUrlDraft] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addUrl = () => {
    const v = urlDraft.trim();
    if (!v) return;
    onChange([...value, v]);
    setUrlDraft('');
  };

  const addFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    Promise.all(
      list.map(
        (f) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result ?? ''));
            reader.onerror = reject;
            reader.readAsDataURL(f);
          })
      )
    ).then((dataUrls) => {
      onChange([...value, ...dataUrls]);
    });
  };

  const removeAt = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const makePrimary = (i: number) => {
    if (i === 0) return;
    const next = value.slice();
    const [moved] = next.splice(i, 1);
    next.unshift(moved);
    onChange(next);
  };

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {label && (
        <span className="text-xs font-medium text-content-secondary">{label}</span>
      )}

      {/* URL + Upload controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-surface-card px-2">
          <LinkIcon className="h-3.5 w-3.5 shrink-0 text-content-muted" />
          <input
            type="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addUrl();
              }
            }}
            placeholder="Paste image URL and press Enter"
            className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-content-muted"
          />
          <button
            type="button"
            onClick={addUrl}
            disabled={!urlDraft.trim()}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary-light disabled:opacity-40"
            aria-label="Add URL"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-10 shrink-0 items-center gap-1 rounded-md border border-border px-3 text-sm text-content-secondary hover:bg-surface-card-hover"
        >
          <Upload className="h-3.5 w-3.5" /> Upload
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* Drop zone + previews */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex min-h-[120px] flex-wrap gap-2 rounded-md border border-dashed p-3 transition-colors',
          dragOver ? 'border-primary bg-primary-light' : 'border-border'
        )}
      >
        {value.length === 0 ? (
          <div className="flex w-full items-center justify-center gap-2 text-xs text-content-muted">
            <ImageOff className="h-4 w-4" />
            Drag &amp; drop images here, upload, or paste URLs above.
          </div>
        ) : (
          value.map((src, i) => (
            <div
              key={`${i}-${src.slice(0, 40)}`}
              className="group relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-card"
            >
              <img
                src={src}
                alt={`Image ${i + 1}`}
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  (e.currentTarget.parentElement?.querySelector('.fallback') as HTMLElement)?.classList.remove('hidden');
                }}
              />
              <div className="fallback hidden absolute inset-0 flex items-center justify-center">
                <ImageOff className="h-6 w-6 text-content-muted" />
              </div>
              {i === 0 && (
                <span className="absolute left-1 top-1 rounded-sm bg-primary px-1 py-0.5 text-[9px] font-semibold uppercase text-primary-foreground">
                  Primary
                </span>
              )}
              <div className="absolute inset-0 flex flex-col items-stretch justify-end gap-0.5 bg-black/0 p-1 opacity-0 transition-opacity group-hover:bg-black/40 group-hover:opacity-100">
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={() => makePrimary(i)}
                    className="rounded-sm bg-surface-card px-1.5 py-0.5 text-[10px] font-medium text-content-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    Make primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label="Remove"
                  className="flex items-center justify-center gap-1 rounded-sm bg-danger px-1.5 py-0.5 text-[10px] font-medium text-white hover:opacity-90"
                >
                  <X className="h-2.5 w-2.5" /> Remove
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {primaryNote && value.length > 1 && (
        <p className="text-[11px] text-content-muted">{primaryNote}</p>
      )}
    </div>
  );
}
